import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Satellite, AlertTriangle, Settings, Play, Check, KeyRound, Zap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Account {
  id: string;
  accountCode: string;
  accountName: string;
  apiEndpoint: string;
  hasCredentials: boolean;
  isActive: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  notes: string | null;
  legalEntityName: string | null;
  vehicleCount: number;
  syncedVehicleCount: number;
}

const STALE_THRESHOLD_MIN = 15;

function syncBadge(a: Account): { tone: string; label: string } {
  if (a.lastSyncStatus === 'failed') {
    return { tone: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/40', label: 'FAILED' };
  }
  if (!a.lastSyncAt) {
    return { tone: 'bg-stone-500/15 text-stone-700 dark:text-stone-400 border-stone-500/40', label: 'NEVER SYNCED' };
  }
  const ageMin = (Date.now() - new Date(a.lastSyncAt).getTime()) / 60_000;
  if (a.lastSyncStatus === 'ok' && ageMin <= STALE_THRESHOLD_MIN) {
    return { tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40', label: 'OK' };
  }
  if (a.lastSyncStatus === 'manual_trigger_pending') {
    return { tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/40', label: 'SYNCING…' };
  }
  return { tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40', label: 'STALE' };
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return sec + ' seconds ago';
  const min = Math.floor(sec / 60);
  if (min < 60) return min + (min === 1 ? ' minute ago' : ' minutes ago');
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr + (hr === 1 ? ' hour ago' : ' hours ago');
  const day = Math.floor(hr / 24);
  if (day < 30) return day + (day === 1 ? ' day ago' : ' days ago');
  return new Date(iso).toLocaleDateString('en-GB');
}

interface MappingHealth {
  coverage: { total_active: number; mapped: number; pct_mapped: string | null };
  mismatches: Array<{ vehicle_id: string; code: string; jasr_plate: string; afaqy_unit_name: string }>;
  orphanAfaqyUnits: Array<{ afaqy_unit_id: string; afaqy_name: string; account_code: string | null }>;
  unmappedVehicles: Array<{ code: string; plate_en: string | null; vehicle_type: string; project_code: string | null }>;
}

export default function AfaqyAccountsPage() {
  const [list, setList] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [credSheet, setCredSheet] = useState<Account | null>(null);
  const [health, setHealth] = useState<MappingHealth | null>(null);
  const [autoMapBusy, setAutoMapBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await apiClient.get<Account[]>('/api/v1/afaqy-accounts');
      setList(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshHealth = useCallback(async () => {
    try {
      const r = await apiClient.get<MappingHealth>('/api/v1/afaqy/mapping-health');
      setHealth(r.data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { refresh(); refreshHealth(); }, [refresh, refreshHealth]);

  async function runAutoMap() {
    setAutoMapBusy(true);
    try {
      const r = await apiClient.post<{ ok: boolean; newlyMapped: number }>('/api/v1/afaqy/fleet/auto-map');
      window.alert(`Auto-map complete — ${r.data.newlyMapped} new mapping(s)`);
      await refreshHealth();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setAutoMapBusy(false);
    }
  }

  async function syncNow(a: Account) {
    try {
      await apiClient.post('/api/v1/afaqy-accounts/' + a.id + '/sync');
      await refresh();
    } catch (err) { window.alert(extractErrorMessage(err)); }
  }

  async function testConnection(a: Account) {
    try {
      const r = await apiClient.post<{ ok: boolean; message?: string; afaqyMessage?: string; reason?: string; detail?: string }>(
        '/api/v1/afaqy-accounts/' + a.id + '/test', {},
      );
      if (r.data.ok) window.alert('✓ Authentication succeeded for ' + a.accountCode);
      else window.alert('✗ Test failed: ' + (r.data.afaqyMessage ?? r.data.detail ?? r.data.reason ?? 'unknown'));
    } catch (err) { window.alert(extractErrorMessage(err)); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Satellite className="h-4 w-4" /> Afaqy accounts</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        {/* PR-AFAQY-MAPPING MAY 13 — mapping health overview. Surfaces the
            three classes of issue (mismatches, orphan Afaqy units, vehicles
            with no GPS) without requiring an SSH session. */}
        {health && (
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Mapping coverage</div>
                <div className="text-2xl font-light mt-0.5">
                  {health.coverage.mapped} / {health.coverage.total_active}
                  <span className="text-base text-muted-foreground ms-2">({health.coverage.pct_mapped ?? '0'}%)</span>
                </div>
              </div>
              <Button onClick={runAutoMap} disabled={autoMapBusy} variant="outline" size="sm">
                {autoMapBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Re-run auto-map
              </Button>
            </div>

            {health.mismatches.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2">
                  Plate text differs · {health.mismatches.length}
                </div>
                <div className="text-xs space-y-1">
                  {health.mismatches.map((m) => (
                    <div key={m.vehicle_id} className="flex items-center gap-2">
                      <span className="font-mono w-20">{m.code}</span>
                      <span>Jasr says <strong>{m.jasr_plate}</strong></span>
                      <span className="text-muted-foreground">·</span>
                      <span>Afaqy says <strong>{m.afaqy_unit_name}</strong></span>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">
                  Likely transliteration error on one side. Correct the plate
                  in Jasr Hub if it's ours, or update it in Afaqy if it's theirs.
                </div>
              </div>
            )}

            {health.orphanAfaqyUnits.length > 0 && (
              <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3">
                <div className="text-xs uppercase tracking-wider text-rose-700 dark:text-rose-400 mb-2">
                  Afaqy GPS units with no Jasr vehicle · {health.orphanAfaqyUnits.length}
                </div>
                <div className="text-xs space-y-1">
                  {health.orphanAfaqyUnits.map((o) => (
                    <div key={o.afaqy_unit_id} className="flex items-center gap-2">
                      <span className="font-mono">{o.afaqy_name}</span>
                      <span className="text-muted-foreground">({o.account_code ?? '—'})</span>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">
                  Either the vehicle isn't in Jasr Hub yet, or the plate is recorded
                  with different letters (often Arabic→Latin transliteration: D↔J etc.).
                </div>
              </div>
            )}

            {health.unmappedVehicles.length > 0 && (
              <details className="rounded-md border border-border p-3">
                <summary className="text-xs uppercase tracking-wider text-muted-foreground cursor-pointer">
                  Vehicles with no GPS coverage · {health.unmappedVehicles.length}
                </summary>
                <div className="text-xs mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
                  {health.unmappedVehicles.map((v) => (
                    <div key={v.code} className="flex items-center gap-1 truncate">
                      <span className="font-mono">{v.code}</span>
                      <span className="text-muted-foreground">{v.plate_en ?? '—'}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">
                  These vehicles have no Afaqy GPS unit assigned. Fuel efficiency
                  for them falls back to the vehicle-type default until GPS arrives.
                </div>
              </details>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : (
          list.map((a) => {
            const m = syncBadge(a);
            return (
              <div key={a.id} className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-medium text-base">{a.accountName}</div>
                    <div className="text-xs text-muted-foreground font-mono">{a.accountCode}</div>
                  </div>
                  <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + m.tone}>{m.label}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
                  <div>
                    <div>Vehicles</div>
                    <div className="text-foreground font-mono">
                      {a.syncedVehicleCount}/{a.vehicleCount}
                      <span className="text-[10px] text-muted-foreground"> syncing</span>
                    </div>
                  </div>
                  <div><div>Endpoint</div><div className="text-foreground font-mono text-[10px] truncate">{a.apiEndpoint}</div></div>
                  <div><div>Credentials</div><div className="text-foreground">{a.hasCredentials ? '✅ saved' : '— not set'}</div></div>
                  <div><div>Active</div><div className="text-foreground">{a.isActive ? 'yes' : 'no'}</div></div>
                </div>
                {a.lastSyncAt && (
                  <div className="text-xs text-muted-foreground">Last sync: <span className="text-foreground">{relativeTime(a.lastSyncAt)}</span> ({new Date(a.lastSyncAt).toLocaleString('en-GB')})</div>
                )}
                {a.notes && <div className="text-xs text-muted-foreground italic">{a.notes}</div>}
                {a.lastSyncStatus === 'failed' && a.lastSyncError && (
                  <div className="text-xs italic text-rose-600 dark:text-rose-400">Last error: {a.lastSyncError}</div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => setCredSheet(a)} className="gap-1.5">
                    <KeyRound className="h-3.5 w-3.5" />
                    {a.hasCredentials ? 'Replace credentials' : 'Set credentials'}
                  </Button>
                  {a.hasCredentials && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => testConnection(a)} className="gap-1.5">
                        <Zap className="h-3.5 w-3.5" /> Test
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => syncNow(a)} className="gap-1.5">
                        <Play className="h-3.5 w-3.5" /> Sync now
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </main>

      {credSheet && (
        <CredSheet account={credSheet} onClose={() => setCredSheet(null)} onSaved={() => { setCredSheet(null); refresh(); }} />
      )}
    </div>
  );
}

function CredSheet({ account, onClose, onSaved }: { account: Account; onClose: () => void; onSaved: () => void }) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (!user.trim() || !pass) { setError('Username and password required'); return; }
    setBusy(true);
    try {
      await apiClient.put('/api/v1/afaqy-accounts/' + account.id + '/credentials', { apiUsername: user.trim(), apiPassword: pass });
      onSaved();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> {account.accountName}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          <div className="text-xs text-muted-foreground">
            Credentials are encrypted at rest with AES-256-GCM. Password field is write-only —
            once saved we never display it again.
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u">Username</Label>
            <Input id="u" autoFocus value={user} onChange={(e) => setUser(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="p">Password</Label>
            <Input id="p" type="password" value={pass} onChange={(e) => setPass(e.target.value)} />
          </div>
          {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}


